import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { confirm } from "../lib/confirm";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Image as ImageIcon,
  Plus,
  Trash2,
  ArrowLeft,
  Save,
  RotateCcw,
  ExternalLink,
  LogOut,
} from "lucide-react";

// Move an item within an array by delta (-1 up, +1 down). Returns new array.
function moveItem(arr, index, delta) {
  const next = [...arr];
  const target = index + delta;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "hp_admin_token";

const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------
function setPath(obj, path, value) {
  // Immutably set obj[path] = value where path is an array of keys/indices
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const isArrayIndex = typeof head === "number";
  const next = isArrayIndex ? [...(obj || [])] : { ...(obj || {}) };
  next[head] = setPath(obj?.[head], rest, value);
  return next;
}

// ---------------------------------------------------------------
// Reusable field components — dark-themed to match Admin look
// ---------------------------------------------------------------
const labelCls = "block text-xs font-medium text-slate-400 mb-1";
const fieldCls = "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500";

function TextField({ label, value, onChange, placeholder, testid, multiline = false, rows = 3 }) {
  return (
    <div className="space-y-1">
      <label className={labelCls}>{label}</label>
      {multiline ? (
        <Textarea
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={fieldCls}
          data-testid={testid}
        />
      ) : (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={fieldCls + " h-9"}
          data-testid={testid}
        />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/homepage/upload`, form, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.data_url);
      toast.success("Afbeelding geüpload");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload mislukt");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1">
      <label className={labelCls}>{label}</label>
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 rounded-md border border-slate-700 bg-slate-900 flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-6 h-6 text-slate-600" />
          )}
        </div>
        <div className="flex flex-col gap-2 flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 file:cursor-pointer hover:file:bg-slate-700"
            data-testid={testid}
          />
          <p className="text-xs text-slate-500">Max 500 KB. JPG, PNG of WebP.</p>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-rose-400 hover:text-rose-300 text-left"
            >
              Verwijderen
            </button>
          )}
          {uploading && <p className="text-xs text-amber-400">Uploaden...</p>}
        </div>
      </div>
    </div>
  );
}

function Section({ title, testid, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-900 hover:bg-slate-900/70 text-left"
        data-testid={testid}
      >
        <span className="font-semibold text-sm">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-4 space-y-4 bg-slate-950">{children}</div>}
    </div>
  );
}

function SubCard({ title, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, children, testid, confirmMessage }) {
  const handleRemove = async () => {
    const ok = await confirm({
      title: `${title} verwijderen?`,
      message: confirmMessage || "Deze actie kan niet ongedaan worden gemaakt. Vergeet niet je wijzigingen op te slaan.",
      confirmLabel: "Verwijderen",
      destructive: true,
    });
    if (ok) onRemove();
  };
  return (
    <div className="border border-slate-800 rounded-md p-3 space-y-3 bg-slate-900/40" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</span>
        <div className="flex items-center gap-1">
          {onMoveUp && (
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              className="text-slate-400 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-slate-400 disabled:cursor-not-allowed p-0.5"
              title="Omhoog verplaatsen"
              data-testid={`${testid}-move-up`}
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
          {onMoveDown && (
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              className="text-slate-400 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-slate-400 disabled:cursor-not-allowed p-0.5"
              title="Omlaag verplaatsen"
              data-testid={`${testid}-move-down`}
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={handleRemove}
              className="text-rose-400 hover:text-rose-300 p-0.5 ml-1"
              data-testid={`${testid}-remove`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function AddItemButton({ onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-dashed border-slate-700 text-slate-400 hover:text-amber-400 hover:border-amber-500"
      data-testid={testid}
    >
      <Plus className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

// ---------------------------------------------------------------
// Main editor page
// ---------------------------------------------------------------
export const HomepageEditor = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/homepage`);
      setContent(res.data);
    } catch {
      toast.error("Inhoud laden mislukt");
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/sportly-admin", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  // Generic setter — path is array, value is new value
  const update = (path, value) => {
    setContent((prev) => setPath(prev, path, value));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/homepage`, content, { headers: adminHeaders() });
      toast.success("Homepage opgeslagen");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessie verlopen. Log opnieuw in.");
        navigate("/sportly-admin", { replace: true });
      } else {
        toast.error(err.response?.data?.detail || "Opslaan mislukt");
      }
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/homepage/reset`, {}, { headers: adminHeaders() });
      toast.success("Teruggezet naar standaardwaarden");
      setResetOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset mislukt");
    } finally {
      setResetting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/sportly-admin", { replace: true });
  };

  const togglePublished = async (nextValue) => {
    const nextContent = { ...(content || {}), published: nextValue };
    setContent(nextContent);
    try {
      await axios.put(`${API}/homepage`, nextContent, { headers: adminHeaders() });
      toast.success(nextValue ? "Homepage staat nu live" : "Homepage is offline (concept)");
    } catch (err) {
      // revert on failure
      setContent((prev) => ({ ...(prev || {}), published: !nextValue }));
      if (err.response?.status === 401) {
        toast.error("Sessie verlopen. Log opnieuw in.");
        navigate("/sportly-admin", { replace: true });
      } else {
        toast.error(err.response?.data?.detail || "Status wijzigen mislukt");
      }
    }
  };

  if (!content) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-700 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="homepage-editor">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/sportly-admin/dashboard")}
              className="text-slate-400 hover:text-white flex items-center gap-1.5"
              data-testid="editor-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Admin</span>
            </button>
            <span className="text-slate-700">/</span>
            <span className="font-bold text-sm">Homepage CMS</span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-slate-700 bg-slate-900"
              data-testid="editor-publish-toggle-wrap"
            >
              <span
                className={`inline-block w-2 h-2 rounded-full ${content.published ? "bg-emerald-400" : "bg-slate-500"}`}
                data-testid="editor-publish-indicator"
              />
              <span className="text-xs text-slate-300 hidden sm:inline">
                {content.published ? "Live" : "Concept"}
              </span>
              <Switch
                checked={!!content.published}
                onCheckedChange={togglePublished}
                className="data-[state=checked]:bg-emerald-500"
                data-testid="editor-publish-switch"
              />
            </div>
            <a
              href={content?.published ? "/" : "/?preview=1"}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5"
              data-testid="editor-preview-btn"
              title={content?.published ? "Open de live homepage in een nieuw tabblad" : "Bekijk hoe de homepage eruit ziet zonder hem live te zetten"}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {content?.published ? "Bekijk live" : "Preview concept"}
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setResetOpen(true)}
              className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 h-9"
              data-testid="editor-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Standaard
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 h-9"
              data-testid="editor-save-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? "Opslaan..." : "Opslaan"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="editor-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-4 pb-24">
        <SeoSection content={content} update={update} />
        <NavSection content={content} update={update} />
        <HeroSection content={content} update={update} />
        <SocialProofSection content={content} update={update} />
        <FeaturesSection content={content} update={update} />
        <HowItWorksSection content={content} update={update} />
        <PricingSection content={content} update={update} />
        <ReviewsSection content={content} update={update} />
        <FAQSection content={content} update={update} />
        <FooterCTASection content={content} update={update} />
        <AboutSection content={content} update={update} />
        <FooterSection content={content} update={update} />
      </main>

      {/* Reset confirmation */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Terugzetten naar standaard?</DialogTitle>
            <DialogDescription className="text-slate-400">
              Alle teksten en afbeeldingen die je hebt aangepast worden gewist. De homepage gebruikt
              dan weer de fabrieksinhoud. Dit kan niet ongedaan worden gemaakt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetOpen(false)}
              className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300"
            >
              Annuleren
            </Button>
            <Button
              onClick={doReset}
              disabled={resetting}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="editor-reset-confirm"
            >
              {resetting ? "Bezig..." : "Ja, terugzetten"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------
// Sections
// ---------------------------------------------------------------
function SeoSection({ content, update }) {
  const s = content.seo || {};
  return (
    <Section title="0. SEO & meta (Google, WhatsApp, LinkedIn delen)" testid="section-seo">
      <p className="text-xs text-slate-500 -mt-1">
        Deze velden bepalen hoe Sportly verschijnt in zoekresultaten en wanneer iemand de
        homepage deelt op WhatsApp, LinkedIn of Twitter.
      </p>
      <TextField
        label="Pagina titel (browser tabblad + Google blauwe link)"
        value={s.title}
        onChange={(v) => update(["seo", "title"], v)}
        testid="seo-title"
        placeholder="Sportly — Hockeyplanner voor je hele team"
      />
      <TextField
        label="Meta description (Google omschrijving — max ~160 tekens)"
        value={s.description}
        onChange={(v) => update(["seo", "description"], v)}
        testid="seo-description"
        multiline
        rows={2}
      />
      <TextField
        label="Keywords (komma-gescheiden, optioneel)"
        value={s.keywords}
        onChange={(v) => update(["seo", "keywords"], v)}
        testid="seo-keywords"
      />

      <div className="border-t border-slate-800 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
          Sociale media (Open Graph) — preview bij delen
        </p>
        <TextField
          label="OG titel (laat leeg om paginatitel hierboven te gebruiken)"
          value={s.og_title}
          onChange={(v) => update(["seo", "og_title"], v)}
          testid="seo-og-title"
        />
        <TextField
          label="OG omschrijving (laat leeg om meta description te gebruiken)"
          value={s.og_description}
          onChange={(v) => update(["seo", "og_description"], v)}
          testid="seo-og-description"
          multiline
          rows={2}
        />
        <TextField
          label="OG URL (volledige URL van je homepage — bv. https://sportly.nl)"
          value={s.og_url}
          onChange={(v) => update(["seo", "og_url"], v)}
          testid="seo-og-url"
          placeholder="https://sportly.nl"
        />
        <ImageField
          label="OG image (1200×630px aanbevolen — verschijnt bij WhatsApp/LinkedIn delen)"
          value={s.og_image}
          onChange={(v) => update(["seo", "og_image"], v)}
          testid="seo-og-image"
        />
        <div className="space-y-1">
          <label className={labelCls}>Twitter card type</label>
          <select
            value={s.twitter_card || "summary_large_image"}
            onChange={(e) => update(["seo", "twitter_card"], e.target.value)}
            className={fieldCls + " h-9 rounded-md border px-3 text-sm w-full"}
            data-testid="seo-twitter-card"
          >
            <option value="summary_large_image">Groot beeld (summary_large_image)</option>
            <option value="summary">Klein beeld (summary)</option>
          </select>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-4">
        <ImageField
          label="Favicon (klein icoon in browser tabblad — bij voorkeur 32×32 PNG)"
          value={s.favicon}
          onChange={(v) => update(["seo", "favicon"], v)}
          testid="seo-favicon"
        />
      </div>
    </Section>
  );
}

function NavSection({ content, update }) {
  const nav = content.nav || {};
  const links = nav.links || [];
  return (
    <Section title="1. Navigatie" testid="section-nav" defaultOpen>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Merknaam (logo wordmark)" value={nav.brand} onChange={(v) => update(["nav", "brand"], v)} testid="nav-brand" />
        <TextField label="Logo letter" value={nav.logo_letter} onChange={(v) => update(["nav", "logo_letter"], v)} testid="nav-logo-letter" />
      </div>

      <div>
        <p className={labelCls}>Navigatie-links</p>
        <div className="space-y-2">
          {links.map((l, i) => (
            <SubCard
              key={i}
              title={`Link ${i + 1}`}
              testid={`nav-link-${i}`}
              onRemove={() => update(["nav", "links"], links.filter((_, j) => j !== i))}
            >
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Label" value={l.label} onChange={(v) => update(["nav", "links", i, "label"], v)} testid={`nav-link-${i}-label`} />
                <TextField label="Anker (#functies, #prijzen, ...)" value={l.href} onChange={(v) => update(["nav", "links", i, "href"], v)} testid={`nav-link-${i}-href`} />
              </div>
            </SubCard>
          ))}
          <AddItemButton
            onClick={() => update(["nav", "links"], [...links, { label: "Nieuw", href: "#" }])}
            label="Link toevoegen"
            testid="nav-add-link"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Inloggen knop label" value={nav.login_label} onChange={(v) => update(["nav", "login_label"], v)} testid="nav-login-label" />
        <TextField label="Inloggen knop link (/login, URL of #anker)" value={nav.login_href} onChange={(v) => update(["nav", "login_href"], v)} testid="nav-login-href" placeholder="/login" />
        <TextField label="CTA knop label (niet ingelogd)" value={nav.cta_label} onChange={(v) => update(["nav", "cta_label"], v)} testid="nav-cta-label" />
        <TextField label="CTA knop link" value={nav.cta_href} onChange={(v) => update(["nav", "cta_href"], v)} testid="nav-cta-href" placeholder="/login" />
        <TextField label="Dashboard knop label (ingelogd)" value={nav.dashboard_label} onChange={(v) => update(["nav", "dashboard_label"], v)} testid="nav-dashboard-label" />
        <TextField label="Dashboard knop link" value={nav.dashboard_href} onChange={(v) => update(["nav", "dashboard_href"], v)} testid="nav-dashboard-href" placeholder="/overview" />
      </div>
    </Section>
  );
}

function HeroSection({ content, update }) {
  const h = content.hero || {};
  return (
    <Section title="2. Hero" testid="section-hero">
      <TextField label="Badge tekst (bovenaan)" value={h.badge} onChange={(v) => update(["hero", "badge"], v)} testid="hero-badge" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Hoofdtitel regel 1" value={h.headline_line1} onChange={(v) => update(["hero", "headline_line1"], v)} testid="hero-h1-1" />
        <TextField label="Hoofdtitel regel 2" value={h.headline_line2} onChange={(v) => update(["hero", "headline_line2"], v)} testid="hero-h1-2" />
      </div>
      <TextField label="Paragraaf" value={h.paragraph} onChange={(v) => update(["hero", "paragraph"], v)} multiline testid="hero-paragraph" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Primaire CTA (knop)" value={h.primary_cta} onChange={(v) => update(["hero", "primary_cta"], v)} testid="hero-primary-cta" />
        <TextField label="Primaire CTA link (/login, URL of #anker)" value={h.primary_href} onChange={(v) => update(["hero", "primary_href"], v)} testid="hero-primary-href" placeholder="/login" />
        <TextField label="Secundaire CTA (knop)" value={h.secondary_cta} onChange={(v) => update(["hero", "secondary_cta"], v)} testid="hero-secondary-cta" />
        <TextField label="Secundaire CTA link" value={h.secondary_href} onChange={(v) => update(["hero", "secondary_href"], v)} testid="hero-secondary-href" placeholder="#functies" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Trust-tekst 1" value={h.trust1} onChange={(v) => update(["hero", "trust1"], v)} testid="hero-trust1" />
        <TextField label="Trust-tekst 2" value={h.trust2} onChange={(v) => update(["hero", "trust2"], v)} testid="hero-trust2" />
      </div>
      <ImageField
        label="Hero visual (vervangt standaard 'Overzicht' mock — laat leeg voor de standaard mock)"
        value={h.mock_image}
        onChange={(v) => update(["hero", "mock_image"], v)}
        testid="hero-mock-upload"
      />
    </Section>
  );
}

function SocialProofSection({ content, update }) {
  const sp = content.social_proof || {};
  const stats = sp.stats || [];
  const clubs = sp.clubs || [];
  return (
    <Section title="3. Social proof (stats + clubs)" testid="section-social">
      <div>
        <p className={labelCls}>Statistieken (4 blokken)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {stats.map((s, i) => (
            <SubCard
              key={i}
              title={`Stat ${i + 1}`}
              testid={`stat-${i}`}
              onRemove={stats.length > 1 ? () => update(["social_proof", "stats"], stats.filter((_, j) => j !== i)) : null}
            >
              <div className="grid grid-cols-3 gap-2">
                <TextField label="Getal" value={s.number} onChange={(v) => update(["social_proof", "stats", i, "number"], v)} testid={`stat-${i}-number`} />
                <TextField label="Label" value={s.label} onChange={(v) => update(["social_proof", "stats", i, "label"], v)} testid={`stat-${i}-label`} />
                <TextField label="Sublabel (bv. ★★★★★)" value={s.sub} onChange={(v) => update(["social_proof", "stats", i, "sub"], v)} testid={`stat-${i}-sub`} />
              </div>
            </SubCard>
          ))}
          {stats.length < 6 && (
            <AddItemButton
              onClick={() => update(["social_proof", "stats"], [...stats, { number: "", label: "", sub: "" }])}
              label="Stat toevoegen"
              testid="add-stat"
            />
          )}
        </div>
      </div>

      <TextField
        label="Label voor club-strip"
        value={sp.logos_label}
        onChange={(v) => update(["social_proof", "logos_label"], v)}
        testid="logos-label"
      />

      <div>
        <p className={labelCls}>Clubs (logo + naam)</p>
        <div className="space-y-2">
          {clubs.map((c, i) => (
            <SubCard
              key={i}
              title={`Club ${i + 1}`}
              testid={`club-${i}`}
              onRemove={() => update(["social_proof", "clubs"], clubs.filter((_, j) => j !== i))}
            >
              <TextField label="Naam" value={c.name} onChange={(v) => update(["social_proof", "clubs", i, "name"], v)} testid={`club-${i}-name`} />
              <ImageField
                label="Logo (optioneel — leeg = standaard monogram)"
                value={c.logo}
                onChange={(v) => update(["social_proof", "clubs", i, "logo"], v)}
                testid={`club-${i}-logo`}
              />
            </SubCard>
          ))}
          <AddItemButton
            onClick={() => update(["social_proof", "clubs"], [...clubs, { name: "Nieuwe club", logo: "" }])}
            label="Club toevoegen"
            testid="add-club"
          />
        </div>
      </div>
    </Section>
  );
}

function FeaturesSection({ content, update }) {
  const f = content.features || {};
  const roles = f.roles || [];
  return (
    <Section title="4. Functies per rol" testid="section-features">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Eyebrow (klein label)" value={f.eyebrow} onChange={(v) => update(["features", "eyebrow"], v)} testid="features-eyebrow" />
        <div />
        <TextField label="Sectietitel regel 1" value={f.headline_line1} onChange={(v) => update(["features", "headline_line1"], v)} testid="features-h2-1" />
        <TextField label="Sectietitel regel 2" value={f.headline_line2} onChange={(v) => update(["features", "headline_line2"], v)} testid="features-h2-2" />
      </div>
      <TextField label="Paragraaf onder titel" value={f.paragraph} onChange={(v) => update(["features", "paragraph"], v)} multiline testid="features-paragraph" />

      <div>
        <p className={labelCls}>Rollen ({roles.length}) — elke rol heeft eigen tab op de homepage</p>
        <div className="space-y-3">
          {roles.map((role, ri) => {
            const enabled = role.enabled !== false; // default true when unset
            return (
            <SubCard
              key={ri}
              title={`Rol: ${role.name}${enabled ? "" : " (uit)"}`}
              testid={`role-${ri}`}
              onRemove={roles.length > 1 ? () => update(["features", "roles"], roles.filter((_, j) => j !== ri)) : null}
              onMoveUp={() => update(["features", "roles"], moveItem(roles, ri, -1))}
              onMoveDown={() => update(["features", "roles"], moveItem(roles, ri, 1))}
              canMoveUp={ri > 0}
              canMoveDown={ri < roles.length - 1}
            >
              <div className="flex items-center justify-between rounded-md bg-slate-950/40 border border-slate-800 px-3 py-2">
                <div>
                  <p className="text-xs font-semibold text-slate-200">Toon op homepage</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Uit = chip & content verborgen op homepage (content blijft bewaard)</p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={(v) => update(["features", "roles", ri, "enabled"], v)}
                  data-testid={`role-${ri}-enabled`}
                />
              </div>
              <TextField
                label="Rolnaam (tab-label)"
                value={role.name}
                onChange={(v) => update(["features", "roles", ri, "name"], v)}
                testid={`role-${ri}-name`}
              />
              <div className="space-y-2">
                <p className={labelCls}>Features ({(role.items || []).length})</p>
                {(role.items || []).map((it, ii) => (
                  <SubCard
                    key={ii}
                    title={`${ii + 1}. ${it.eyebrow || "—"}`}
                    testid={`role-${ri}-feature-${ii}`}
                    onRemove={() => update(["features", "roles", ri, "items"], (role.items || []).filter((_, j) => j !== ii))}
                    onMoveUp={() => update(["features", "roles", ri, "items"], moveItem(role.items || [], ii, -1))}
                    onMoveDown={() => update(["features", "roles", ri, "items"], moveItem(role.items || [], ii, 1))}
                    canMoveUp={ii > 0}
                    canMoveDown={ii < (role.items || []).length - 1}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <TextField
                        label="Eyebrow (klein label)"
                        value={it.eyebrow}
                        onChange={(v) => update(["features", "roles", ri, "items", ii, "eyebrow"], v)}
                        testid={`feat-${ri}-${ii}-eyebrow`}
                      />
                      <TextField
                        label="Titel"
                        value={it.title}
                        onChange={(v) => update(["features", "roles", ri, "items", ii, "title"], v)}
                        testid={`feat-${ri}-${ii}-title`}
                      />
                    </div>
                    <TextField
                      label="Body tekst"
                      value={it.body}
                      onChange={(v) => update(["features", "roles", ri, "items", ii, "body"], v)}
                      multiline
                      testid={`feat-${ri}-${ii}-body`}
                    />
                    <BulletsEditor
                      bullets={it.bullets || []}
                      onChange={(arr) => update(["features", "roles", ri, "items", ii, "bullets"], arr)}
                      testidPrefix={`feat-${ri}-${ii}`}
                    />
                    <ImageField
                      label="Afbeelding (leeg = grijs placeholder)"
                      value={it.image}
                      onChange={(v) => update(["features", "roles", ri, "items", ii, "image"], v)}
                      testid={`feat-${ri}-${ii}-img`}
                    />
                  </SubCard>
                ))}
                <AddItemButton
                  onClick={() => update(["features", "roles", ri, "items"], [...(role.items || []), { eyebrow: "Nieuw", title: "", body: "", bullets: [], image: "" }])}
                  label="Feature toevoegen"
                  testid={`role-${ri}-add-feature`}
                />
              </div>
            </SubCard>
            );
          })}
          <AddItemButton
            onClick={() => update(["features", "roles"], [...roles, { name: "Nieuwe rol", enabled: true, items: [] }])}
            label="Rol toevoegen"
            testid="add-role"
          />
        </div>
      </div>
    </Section>
  );
}

function BulletsEditor({ bullets, onChange, testidPrefix }) {
  return (
    <div>
      <p className={labelCls}>Bullets</p>
      <div className="space-y-2">
        {bullets.map((b, i) => (
          <div key={i} className="flex gap-2 items-start">
            <Input
              value={b}
              onChange={(e) => {
                const next = [...bullets];
                next[i] = e.target.value;
                onChange(next);
              }}
              className={fieldCls + " h-9 flex-1"}
              data-testid={`${testidPrefix}-bullet-${i}`}
            />
            <div className="flex items-center pt-1">
              <button
                type="button"
                onClick={() => onChange(moveItem(bullets, i, -1))}
                disabled={i === 0}
                className="text-slate-400 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-slate-400 disabled:cursor-not-allowed p-1"
                title="Omhoog verplaatsen"
                data-testid={`${testidPrefix}-bullet-${i}-up`}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onChange(moveItem(bullets, i, 1))}
                disabled={i === bullets.length - 1}
                className="text-slate-400 hover:text-amber-400 disabled:opacity-30 disabled:hover:text-slate-400 disabled:cursor-not-allowed p-1"
                title="Omlaag verplaatsen"
                data-testid={`${testidPrefix}-bullet-${i}-down`}
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await confirm({
                    title: "Bullet verwijderen?",
                    message: "Deze bullet-tekst wordt verwijderd. Vergeet niet je wijzigingen op te slaan.",
                    confirmLabel: "Verwijderen",
                    destructive: true,
                  });
                  if (ok) onChange(bullets.filter((_, j) => j !== i));
                }}
                className="text-rose-400 hover:text-rose-300 p-1 ml-1"
                data-testid={`${testidPrefix}-bullet-${i}-remove`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        <AddItemButton onClick={() => onChange([...bullets, ""])} label="Bullet toevoegen" testid={`${testidPrefix}-add-bullet`} />
      </div>
    </div>
  );
}

function HowItWorksSection({ content, update }) {
  const h = content.how_it_works || {};
  const steps = h.steps || [];
  return (
    <Section title="5. Hoe het werkt (3 stappen)" testid="section-how">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Eyebrow" value={h.eyebrow} onChange={(v) => update(["how_it_works", "eyebrow"], v)} testid="how-eyebrow" />
        <TextField label="Sectietitel" value={h.headline} onChange={(v) => update(["how_it_works", "headline"], v)} testid="how-headline" />
      </div>
      <div className="space-y-2">
        {steps.map((s, i) => (
          <SubCard
            key={i}
            title={`Stap ${i + 1}`}
            testid={`step-${i}`}
            onRemove={steps.length > 1 ? () => update(["how_it_works", "steps"], steps.filter((_, j) => j !== i)) : null}
          >
            <div className="grid grid-cols-3 gap-3">
              <TextField label="Nummer/cijfer" value={s.number} onChange={(v) => update(["how_it_works", "steps", i, "number"], v)} testid={`step-${i}-num`} />
              <div className="col-span-2">
                <TextField label="Titel" value={s.title} onChange={(v) => update(["how_it_works", "steps", i, "title"], v)} testid={`step-${i}-title`} />
              </div>
            </div>
            <TextField label="Body" value={s.body} onChange={(v) => update(["how_it_works", "steps", i, "body"], v)} multiline testid={`step-${i}-body`} />
          </SubCard>
        ))}
        <AddItemButton
          onClick={() => update(["how_it_works", "steps"], [...steps, { number: String(steps.length + 1), title: "", body: "" }])}
          label="Stap toevoegen"
          testid="add-step"
        />
      </div>
    </Section>
  );
}

function PricingSection({ content, update }) {
  const p = content.pricing || {};
  const tiers = p.tiers || [];
  return (
    <Section title="6. Prijzen" testid="section-pricing">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <TextField label="Eyebrow" value={p.eyebrow} onChange={(v) => update(["pricing", "eyebrow"], v)} testid="pricing-eyebrow" />
        <TextField label="Titel" value={p.headline} onChange={(v) => update(["pricing", "headline"], v)} testid="pricing-headline" />
        <TextField label="Subtitle" value={p.subtitle} onChange={(v) => update(["pricing", "subtitle"], v)} testid="pricing-subtitle" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Toggle 'Maandelijks' label" value={p.toggle_monthly} onChange={(v) => update(["pricing", "toggle_monthly"], v)} testid="pricing-toggle-monthly" />
        <TextField label="Toggle 'Jaarlijks' label" value={p.toggle_yearly} onChange={(v) => update(["pricing", "toggle_yearly"], v)} testid="pricing-toggle-yearly" />
      </div>

      <div className="space-y-2">
        <p className={labelCls}>Prijs-tiers</p>
        {tiers.map((t, i) => (
          <SubCard
            key={i}
            title={`Tier: ${t.name || "—"}`}
            testid={`tier-${i}`}
            onRemove={tiers.length > 1 ? () => update(["pricing", "tiers"], tiers.filter((_, j) => j !== i)) : null}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Naam" value={t.name} onChange={(v) => update(["pricing", "tiers", i, "name"], v)} testid={`tier-${i}-name`} />
              <TextField label="Badge (bv. 'Populairste') — leeg = geen badge" value={t.badge} onChange={(v) => update(["pricing", "tiers", i, "badge"], v)} testid={`tier-${i}-badge`} />
              <TextField label="Prijs (maandelijks)" value={t.price_monthly} onChange={(v) => update(["pricing", "tiers", i, "price_monthly"], v)} testid={`tier-${i}-pm`} />
              <TextField label="Prijs (jaarlijks)" value={t.price_yearly} onChange={(v) => update(["pricing", "tiers", i, "price_yearly"], v)} testid={`tier-${i}-py`} />
              <TextField label="Subtekst (maandelijks)" value={t.sub_monthly} onChange={(v) => update(["pricing", "tiers", i, "sub_monthly"], v)} testid={`tier-${i}-sm`} />
              <TextField label="Subtekst (jaarlijks)" value={t.sub_yearly} onChange={(v) => update(["pricing", "tiers", i, "sub_yearly"], v)} testid={`tier-${i}-sy`} />
              <TextField label="CTA knop label" value={t.cta} onChange={(v) => update(["pricing", "tiers", i, "cta"], v)} testid={`tier-${i}-cta`} />
              <TextField label="CTA knop link (/login, URL of mailto:)" value={t.cta_href} onChange={(v) => update(["pricing", "tiers", i, "cta_href"], v)} testid={`tier-${i}-cta-href`} placeholder="/login" />
              <div className="space-y-1">
                <label className={labelCls}>Donkere highlight-tier?</label>
                <select
                  value={t.highlight ? "yes" : "no"}
                  onChange={(e) => update(["pricing", "tiers", i, "highlight"], e.target.value === "yes")}
                  className={fieldCls + " h-9 rounded-md border px-3 text-sm w-full"}
                  data-testid={`tier-${i}-highlight`}
                >
                  <option value="no">Nee</option>
                  <option value="yes">Ja (zwarte achtergrond)</option>
                </select>
              </div>
            </div>
            <BulletsEditor
              bullets={t.features || []}
              onChange={(arr) => update(["pricing", "tiers", i, "features"], arr)}
              testidPrefix={`tier-${i}`}
            />
          </SubCard>
        ))}
        <AddItemButton
          onClick={() =>
            update(["pricing", "tiers"], [
              ...tiers,
              {
                name: "Nieuwe tier",
                price_monthly: "€0",
                price_yearly: "€0",
                sub_monthly: "",
                sub_yearly: "",
                cta: "Probeer",
                cta_href: "/login",
                badge: "",
                highlight: false,
                features: [],
              },
            ])
          }
          label="Tier toevoegen"
          testid="add-tier"
        />
      </div>
    </Section>
  );
}

function ReviewsSection({ content, update }) {
  const r = content.reviews || {};
  const items = r.items || [];
  return (
    <Section title="7. Reviews & download" testid="section-reviews">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Eyebrow" value={r.eyebrow} onChange={(v) => update(["reviews", "eyebrow"], v)} testid="reviews-eyebrow" />
        <TextField label="Titel" value={r.headline} onChange={(v) => update(["reviews", "headline"], v)} testid="reviews-headline" />
      </div>
      <div className="space-y-2">
        <p className={labelCls}>Reviews</p>
        {items.map((it, i) => (
          <SubCard
            key={i}
            title={`Review ${i + 1}: ${it.name || ""}`}
            testid={`review-${i}`}
            onRemove={() => update(["reviews", "items"], items.filter((_, j) => j !== i))}
          >
            <TextField label="Quote" value={it.quote} onChange={(v) => update(["reviews", "items", i, "quote"], v)} multiline testid={`review-${i}-quote`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Naam" value={it.name} onChange={(v) => update(["reviews", "items", i, "name"], v)} testid={`review-${i}-name`} />
              <TextField label="Rol" value={it.role} onChange={(v) => update(["reviews", "items", i, "role"], v)} testid={`review-${i}-role`} />
              <TextField label="Initialen (bv. 'MV')" value={it.initials} onChange={(v) => update(["reviews", "items", i, "initials"], v)} testid={`review-${i}-init`} />
              <TextField label="Avatar kleur (CSS variabele of hex)" value={it.color} onChange={(v) => update(["reviews", "items", i, "color"], v)} testid={`review-${i}-color`} />
            </div>
          </SubCard>
        ))}
        <AddItemButton
          onClick={() =>
            update(["reviews", "items"], [
              ...items,
              { quote: "", name: "", role: "", initials: "AB", color: "var(--pitch-600)" },
            ])
          }
          label="Review toevoegen"
          testid="add-review"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Download titel" value={r.download_title} onChange={(v) => update(["reviews", "download_title"], v)} testid="reviews-dl-title" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <TextField label="App Store subtitle" value={r.apple_subtitle} onChange={(v) => update(["reviews", "apple_subtitle"], v)} testid="reviews-apple-sub" />
        <TextField label="App Store label" value={r.apple_label} onChange={(v) => update(["reviews", "apple_label"], v)} testid="reviews-apple-label" />
        <TextField label="App Store URL" value={r.apple_href} onChange={(v) => update(["reviews", "apple_href"], v)} testid="reviews-apple-href" />
        <TextField label="Google Play subtitle" value={r.google_subtitle} onChange={(v) => update(["reviews", "google_subtitle"], v)} testid="reviews-google-sub" />
        <TextField label="Google Play label" value={r.google_label} onChange={(v) => update(["reviews", "google_label"], v)} testid="reviews-google-label" />
        <TextField label="Google Play URL" value={r.google_href} onChange={(v) => update(["reviews", "google_href"], v)} testid="reviews-google-href" />
      </div>
    </Section>
  );
}

function FAQSection({ content, update }) {
  const f = content.faq || {};
  const items = f.items || [];
  return (
    <Section title="8. FAQ" testid="section-faq">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Eyebrow" value={f.eyebrow} onChange={(v) => update(["faq", "eyebrow"], v)} testid="faq-eyebrow" />
        <TextField label="Titel" value={f.headline} onChange={(v) => update(["faq", "headline"], v)} testid="faq-headline" />
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <SubCard
            key={i}
            title={`Q${i + 1}: ${(it.question || "").slice(0, 40)}`}
            testid={`faq-${i}`}
            onRemove={() => update(["faq", "items"], items.filter((_, j) => j !== i))}
          >
            <TextField label="Vraag" value={it.question} onChange={(v) => update(["faq", "items", i, "question"], v)} testid={`faq-${i}-q`} />
            <TextField label="Antwoord" value={it.answer} onChange={(v) => update(["faq", "items", i, "answer"], v)} multiline testid={`faq-${i}-a`} />
          </SubCard>
        ))}
        <AddItemButton
          onClick={() => update(["faq", "items"], [...items, { question: "", answer: "" }])}
          label="Vraag toevoegen"
          testid="add-faq"
        />
      </div>
    </Section>
  );
}

function FooterCTASection({ content, update }) {
  const c = content.footer_cta || {};
  return (
    <Section title="9. Footer CTA (donker blok)" testid="section-footer-cta">
      <TextField label="Eyebrow" value={c.eyebrow} onChange={(v) => update(["footer_cta", "eyebrow"], v)} testid="fcta-eyebrow" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Titel regel 1" value={c.headline_line1} onChange={(v) => update(["footer_cta", "headline_line1"], v)} testid="fcta-h2-1" />
        <TextField label="Titel regel 2" value={c.headline_line2} onChange={(v) => update(["footer_cta", "headline_line2"], v)} testid="fcta-h2-2" />
      </div>
      <TextField label="Paragraaf" value={c.paragraph} onChange={(v) => update(["footer_cta", "paragraph"], v)} multiline testid="fcta-paragraph" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Primaire CTA label" value={c.primary_cta} onChange={(v) => update(["footer_cta", "primary_cta"], v)} testid="fcta-primary" />
        <TextField label="Primaire CTA link (/login, URL of #anker)" value={c.primary_href} onChange={(v) => update(["footer_cta", "primary_href"], v)} testid="fcta-primary-href" placeholder="/login" />
        <TextField label="Secundaire CTA label" value={c.secondary_cta} onChange={(v) => update(["footer_cta", "secondary_cta"], v)} testid="fcta-secondary" />
        <TextField label="Secundaire CTA link" value={c.secondary_href} onChange={(v) => update(["footer_cta", "secondary_href"], v)} testid="fcta-secondary-href" placeholder="#functies" />
      </div>
      <TextField label="Geruststelling tekst" value={c.reassurance} onChange={(v) => update(["footer_cta", "reassurance"], v)} testid="fcta-reassurance" />
    </Section>
  );
}

function AboutSection({ content, update }) {
  const a = content.about || {};
  const stats = a.stats || [];
  return (
    <Section title="10. Over ons" testid="section-about">
      <TextField label="Eyebrow" value={a.eyebrow} onChange={(v) => update(["about", "eyebrow"], v)} testid="about-eyebrow" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Titel regel 1" value={a.headline_line1} onChange={(v) => update(["about", "headline_line1"], v)} testid="about-h2-1" />
        <TextField label="Titel regel 2 (groene accent)" value={a.headline_line2} onChange={(v) => update(["about", "headline_line2"], v)} testid="about-h2-2" />
      </div>
      <TextField label="Paragraaf 1" value={a.paragraph1} onChange={(v) => update(["about", "paragraph1"], v)} multiline testid="about-p1" />
      <TextField label="Paragraaf 2" value={a.paragraph2} onChange={(v) => update(["about", "paragraph2"], v)} multiline testid="about-p2" />

      <div>
        <p className={labelCls}>Statistieken</p>
        <div className="space-y-2">
          {stats.map((s, i) => (
            <SubCard
              key={i}
              title={`Stat ${i + 1}`}
              testid={`about-stat-${i}`}
              onRemove={() => update(["about", "stats"], stats.filter((_, j) => j !== i))}
            >
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Getal" value={s.number} onChange={(v) => update(["about", "stats", i, "number"], v)} testid={`about-stat-${i}-num`} />
                <TextField label="Label" value={s.label} onChange={(v) => update(["about", "stats", i, "label"], v)} testid={`about-stat-${i}-label`} />
              </div>
            </SubCard>
          ))}
          <AddItemButton
            onClick={() => update(["about", "stats"], [...stats, { number: "", label: "" }])}
            label="Stat toevoegen"
            testid="add-about-stat"
          />
        </div>
      </div>

      <ImageField label="Teamfoto" value={a.team_image} onChange={(v) => update(["about", "team_image"], v)} testid="about-team-image" />
    </Section>
  );
}

function FooterSection({ content, update }) {
  const f = content.footer || {};
  const links = f.links || [];
  return (
    <Section title="11. Footer" testid="section-footer">
      <TextField label="Copyright tekst" value={f.copyright} onChange={(v) => update(["footer", "copyright"], v)} testid="footer-copyright" />
      <div>
        <p className={labelCls}>Footer links</p>
        <div className="space-y-2">
          {links.map((l, i) => (
            <SubCard
              key={i}
              title={`Link ${i + 1}`}
              testid={`footer-link-${i}`}
              onRemove={() => update(["footer", "links"], links.filter((_, j) => j !== i))}
            >
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Label" value={l.label} onChange={(v) => update(["footer", "links", i, "label"], v)} testid={`footer-link-${i}-label`} />
                <TextField label="URL" value={l.href} onChange={(v) => update(["footer", "links", i, "href"], v)} testid={`footer-link-${i}-href`} />
              </div>
            </SubCard>
          ))}
          <AddItemButton
            onClick={() => update(["footer", "links"], [...links, { label: "", href: "" }])}
            label="Link toevoegen"
            testid="add-footer-link"
          />
        </div>
      </div>
    </Section>
  );
}

export default HomepageEditor;
